import { useEffect, useState } from 'preact/hooks';

import type { Api } from './api';
import { CheckSession } from './check-session';
import { createFeedback } from './feedback';
import { QueueFlusher } from './offline/flush';
import { SnapshotSync } from './offline/sync';
import { SnapshotStore } from './offline/store';
import { ScreenLock } from './screen-lock';
import type { Station } from './types';
import { CheckView } from './views/CheckView';
import { JoinView } from './views/JoinView';
import { LockView } from './views/LockView';

const STATION_KEY = 'jalsa.duty.station';

export interface AppProps {
  api: Api;
  lock?: ScreenLock;
  session?: CheckSession;
  sync?: SnapshotSync;
}

export function App({ api, lock: providedLock, session: providedSession, sync: providedSync }: AppProps) {
  const [feedback] = useState(() => createFeedback());
  const [lock] = useState(() => providedLock ?? new ScreenLock());
  const [store] = useState(() => new SnapshotStore());
  const [sync] = useState(() => providedSync ?? new SnapshotSync(api, store));
  const [flusher] = useState(() => new QueueFlusher(api, store));
  const [session] = useState(
    () => providedSession ?? new CheckSession(api, feedback, { local: sync, queue: store }),
  );
  const [station, setStation] = useState<Station | null>(readStation());

  // Die Sperre prüft sich selbst. Ohne Timer bliebe der Bildschirm offen,
  // solange niemand ihn berührt, also genau im Fall des abgelegten Geräts.
  useEffect(() => {
    const timer = setInterval(() => lock.tick(), 5_000);
    return () => clearInterval(timer);
  }, [lock]);

  // Beim Start prüfen, ob das Gerät überhaupt noch angemeldet ist. Wurde es
  // im Office gesperrt, landet es hier sofort wieder auf der Anmeldung
  // statt in einer Fehlerschleife am Eingang.
  useEffect(() => {
    if (!station) return;
    void api.me().then((outcome) => {
      if (!outcome.ok && outcome.kind === 'unauthorized') void forget();
    });
    // Der gespeicherte Abzug ist sofort einsatzbereit, auch ohne Netz;
    // Änderungen zieht der Sync im Hintergrund nach.
    void sync.start();
    void flusher.start();
    return () => {
      sync.stop();
      flusher.stop();
    };
  }, []);

  // Ein 401 während des Betriebs bedeutet dasselbe.
  useEffect(() => {
    if (session.phase.value.kind === 'locked-out') void forget();
  }, [session.phase.value.kind]);

  /** Gerät gesperrt oder abgemeldet: nichts Entschlüsselbares zurücklassen. */
  async function forget(): Promise<void> {
    localStorage.removeItem(STATION_KEY);
    lock.clearPin();
    flusher.stop();
    await sync.wipe();
    setStation(null);
  }

  if (!station) {
    return (
      <JoinView
        api={api}
        onJoined={(joined, pin) => {
          localStorage.setItem(STATION_KEY, JSON.stringify(joined));
          lock.setPin(pin);
          feedback.unlock();
          setStation(joined);
        }}
      />
    );
  }

  if (lock.locked.value) return <LockView lock={lock} stationName={station.name} />;

  return (
    <CheckView
      session={session}
      station={station}
      pending={flusher.pending.value}
      online={flusher.online.value}
      onActivity={() => lock.touch()}
      onLock={() => lock.lock()}
    />
  );
}

function readStation(): Station | null {
  try {
    const raw = localStorage.getItem(STATION_KEY);
    return raw ? (JSON.parse(raw) as Station) : null;
  } catch {
    return null;
  }
}
